import { create } from 'zustand'
import { Agent } from '../types'
import { genId, nowIso } from '../utils/format'

export interface AgentDraft {
  name: string
  description: string
  systemPrompt: string
  skillIds: string[]
  knowledgeBaseIds: string[]
  defaultModelId: string
  tags: string[]
  retrievalConfig?: {
    topK: number
    scoreThreshold: number
    showCitations: boolean
  }
  runtimeConfig: {
    maxExecutionSeconds?: number
    allowFileAccess?: boolean
    allowNetworkAccess?: boolean
    toolApprovalMode?: 'auto' | 'manual' | 'restricted'
  }
}

interface AgentStore {
  agents: Agent[]
  createAgent: (
    draft: AgentDraft,
    skillsResolver: (ids: string[]) => Agent['skills'],
    knowledgeBaseResolver: (ids: string[]) => Agent['knowledgeBases'],
  ) => string
  updateAgent: (
    id: string,
    draft: AgentDraft,
    skillsResolver: (ids: string[]) => Agent['skills'],
    knowledgeBaseResolver: (ids: string[]) => Agent['knowledgeBases'],
  ) => void
  deleteAgent: (id: string) => void
  duplicateAgent: (id: string) => string
  activateAgent: (id: string) => void
  touchAgent: (id: string) => void
}

const seed: Agent[] = [
  {
    agentId: 'agent_devops',
    name: '运维助手',
    description: '帮助排查服务器问题、分析日志并给出修复建议',
    systemPrompt:
      '你是一名资深 DevOps 工程师，擅长 Linux 运维与故障排查。回答简洁、可执行。',
    skillIds: ['skill_ops_linux', 'skill_nginx_log'],
    skills: [
      {
        skillId: 'skill_ops_linux',
        name: 'Linux 运维 Skill',
        description: '服务器状态检查与故障排查',
        version: '1.0.0',
      },
      {
        skillId: 'skill_nginx_log',
        name: 'Nginx 日志分析 Skill',
        description: '解析 Nginx 日志',
        version: '0.3.2',
      },
    ],
    knowledgeBaseIds: ['kb_product_docs'],
    knowledgeBases: [
      { knowledgeBaseId: 'kb_product_docs', name: '产品文档库' },
    ],
    defaultModelId: 'model_claude_sonnet',
    status: 'active',
    tags: ['ops', 'linux'],
    retrievalConfig: { topK: 5, scoreThreshold: 0.7, showCitations: true },
    runtimeConfig: {
      maxExecutionSeconds: 300,
      allowFileAccess: true,
      allowNetworkAccess: true,
      toolApprovalMode: 'auto',
    },
    createdAt: '2026-06-01T13:00:00Z',
    updatedAt: '2026-06-04T11:00:00Z',
    lastUsedAt: '2026-06-05T08:30:00Z',
  },
  {
    agentId: 'agent_codehelper',
    name: '代码开发助手',
    description: '编写、解释和审查代码，支持多种语言',
    systemPrompt:
      '你是一名全栈工程师，编写高质量、可维护的代码，并解释关键决策。',
    skillIds: ['skill_code_review'],
    skills: [
      {
        skillId: 'skill_code_review',
        name: '代码审查 Skill',
        description: '静态分析与改进建议',
        version: '2.1.0',
      },
    ],
    knowledgeBaseIds: [],
    knowledgeBases: [],
    defaultModelId: 'model_gpt4o',
    status: 'active',
    tags: ['dev', 'code'],
    runtimeConfig: { maxExecutionSeconds: 600, toolApprovalMode: 'manual' },
    createdAt: '2026-06-02T10:00:00Z',
    updatedAt: '2026-06-03T16:00:00Z',
    lastUsedAt: '2026-06-04T19:20:00Z',
  },
  {
    agentId: 'agent_dhuman',
    name: '数字人口播 Agent',
    description: '将营销文案与脚本一键生成数字人口播视频',
    systemPrompt:
      '你是一名数字人口播视频制作助手。根据用户提供的文案脚本，调用数字人口播生成 Skill 产出视频，并确认形象、音色与字幕等参数。回答简洁、专业。',
    skillIds: ['skill_digital_human'],
    skills: [
      {
        skillId: 'skill_digital_human',
        name: '数字人口播生成 Skill',
        description: '根据文案脚本生成数字人口播视频',
        version: '1.2.0',
      },
    ],
    knowledgeBaseIds: [],
    knowledgeBases: [],
    defaultModelId: 'model_claude_sonnet',
    status: 'active',
    tags: ['video', 'marketing', 'digital-human'],
    runtimeConfig: {
      maxExecutionSeconds: 600,
      allowFileAccess: true,
      allowNetworkAccess: true,
      toolApprovalMode: 'auto',
    },
    createdAt: '2026-05-31T09:00:00Z',
    updatedAt: '2026-06-04T18:00:00Z',
    lastUsedAt: '2026-06-05T10:15:00Z',
  },
  {
    agentId: 'agent_research',
    name: '调研分析 Agent',
    description: '收集信息、整理要点并产出结构化报告',
    systemPrompt: '你是一名研究分析师，善于整理信息并产出结构化报告。',
    skillIds: [],
    skills: [],
    knowledgeBaseIds: [],
    knowledgeBases: [],
    defaultModelId: 'model_claude_sonnet',
    status: 'draft',
    tags: ['research'],
    runtimeConfig: { toolApprovalMode: 'restricted' },
    createdAt: '2026-06-04T12:00:00Z',
    updatedAt: '2026-06-04T12:00:00Z',
  },
]

export const useAgentStore = create<AgentStore>((set, get) => ({
  agents: seed,
  createAgent: (draft, resolver, kbResolver) => {
    const agentId = genId('agent')
    set((s) => ({
      agents: [
        ...s.agents,
        {
          agentId,
          name: draft.name,
          description: draft.description,
          systemPrompt: draft.systemPrompt,
          skillIds: draft.skillIds,
          skills: resolver(draft.skillIds),
          knowledgeBaseIds: draft.knowledgeBaseIds,
          knowledgeBases: kbResolver(draft.knowledgeBaseIds),
          defaultModelId: draft.defaultModelId,
          status: 'draft',
          tags: draft.tags,
          retrievalConfig: draft.retrievalConfig,
          runtimeConfig: draft.runtimeConfig,
          createdAt: nowIso(),
          updatedAt: nowIso(),
        },
      ],
    }))
    return agentId
  },
  updateAgent: (id, draft, resolver, kbResolver) =>
    set((s) => ({
      agents: s.agents.map((a) =>
        a.agentId === id
          ? {
              ...a,
              name: draft.name,
              description: draft.description,
              systemPrompt: draft.systemPrompt,
              skillIds: draft.skillIds,
              skills: resolver(draft.skillIds),
              knowledgeBaseIds: draft.knowledgeBaseIds,
              knowledgeBases: kbResolver(draft.knowledgeBaseIds),
              defaultModelId: draft.defaultModelId,
              tags: draft.tags,
              retrievalConfig: draft.retrievalConfig,
              runtimeConfig: draft.runtimeConfig,
              updatedAt: nowIso(),
            }
          : a,
      ),
    })),
  deleteAgent: (id) =>
    set((s) => ({ agents: s.agents.filter((a) => a.agentId !== id) })),
  duplicateAgent: (id) => {
    const src = get().agents.find((a) => a.agentId === id)
    if (!src) return ''
    const agentId = genId('agent')
    set((s) => ({
      agents: [
        ...s.agents,
        {
          ...src,
          agentId,
          name: `${src.name} 副本`,
          status: 'draft',
          createdAt: nowIso(),
          updatedAt: nowIso(),
          lastUsedAt: undefined,
        },
      ],
    }))
    return agentId
  },
  activateAgent: (id) =>
    set((s) => ({
      agents: s.agents.map((a) =>
        a.agentId === id ? { ...a, status: 'active', updatedAt: nowIso() } : a,
      ),
    })),
  touchAgent: (id) =>
    set((s) => ({
      agents: s.agents.map((a) =>
        a.agentId === id ? { ...a, lastUsedAt: nowIso() } : a,
      ),
    })),
}))
